import type { EntityFunctionMap, EntityTypename } from '../../types';
import {
	getAtlassianAccountUserLink,
	// getAtlassianStagingAccountUserLink,
	isAtlassianAccountUser,
} from './atlassianAccountUser';
import { getAtlassianUserLink, isAtlassianUser } from './atlassianUser';
import { getBitbucketRepositoryLink, isBitbucketRepository } from './bitbucketRepository';
import { getCompassComponentLink, isCompassComponent } from './compassComponent';
import { getCompassComponentLinkLink, isCompassComponentLink } from './compassComponentLink';
import { getCompassScorecardLink, isCompassScorecard } from './compassScorecard';
import { getConfluenceBlogPostLink, isConfluenceBlogPost } from './confluenceBlogPost';
import { getConfluenceCommentLink, isConfluenceComment } from './confluenceComment';
import { getConfluenceDatabaseLink, isConfluenceDatabase } from './confluenceDatabase';
import { getConfluenceEmbedLink, isConfluenceEmbed } from './confluenceEmbed';
import { getConfluenceFolderLink, isConfluenceFolder } from './confluenceFolder';
import {
	getConfluenceFooterCommentLink,
	isConfluenceFooterComment,
} from './confluenceFooterComment';
import {
	getConfluenceInlineCommentLink,
	isConfluenceInlineComment,
} from './confluenceInlineComment';
import { getConfluencePageLink, isConfluencePage } from './confluencePage';
import { getConfluenceSpaceLink, isConfluenceSpace } from './confluenceSpace';
import { getConfluenceWhiteboardLink, isConfluenceWhiteboard } from './confluenceWhiteboard';
import { getExternalBranchLink, isExternalBranch } from './externalBranch';
import { getExternalCalendarEventLink, isExternalCalendarEvent } from './externalCalendarEvent';
import { getExternalCommitLink, isExternalCommit } from './externalCommit';
import { getExternalDocumentLink, isExternalDocument } from './externalDocument';
import { getExternalMessageLink, isExternalMessage } from './externalMessage';
import { getExternalPullRequestLink, isExternalPullRequest } from './externalPullRequest';
import { getExternalRepositoryLink, isExternalRepository } from './externalRepository';
import { getExternalWorkItemLink, isExternalWorkItem } from './externalWorkItem';
import { getJiraAlignProjectLink, isJiraAlignProject } from './jiraAlignProject';
import { getJiraBoardLink, isJiraBoard } from './jiraBoard';
import { getJiraCommentLink, isJiraComment } from './jiraComment';
import {
	getJiraCustomRemoteIssueLink,
	isJiraCustomRemoteIssueLink,
} from './jiraCustomRemoteIssueLink';
import { getJiraIssueLink, isJiraIssue } from './jiraIssue';
import {
	getJiraIssueRemoteIssueLink,
	isJiraIssueRemoteIssueLink,
} from './jiraIssueRemoteIssueLink';
import { getJiraPlatformCommentLink, isJiraPlatformComment } from './jiraPlatformComment';
import {
	getJiraPostIncidentReviewLink,
	isJiraPostIncidentReviewLink,
} from './jiraPostIncidentReviewLink';
import { getJiraProjectLink, isJiraProject } from './jiraProject';
import {
	getJiraServiceManagementCommentLink,
	isJiraServiceManagementComment,
} from './jiraServiceManagementComment';
import { getJiraWebRemoteIssueLink, isJiraWebRemoteIssueLink } from './jiraWebRemoteIssueLink';
import { getLoomCommentLink, isLoomComment } from './loomComment';
import { getLoomSpaceLink, isLoomSpace } from './loomSpace';
import { getLoomVideoLink, isLoomVideo } from './loomVideo';
import { getMercuryFocusAreaLink, isMercuryFocusArea } from './mercuryFocusArea';
import { getRovoAgentLink, isAgentStudioAssistant, isAgentStudioServiceAgent } from './rovoAgent';
import { getTeamV2Link, isTeamV2 } from './teamV2';
import { getTownsquareCommentLink, isTownsquareComment } from './townsquareComment';
import { getTownsquareGoalLink, isTownsquareGoal } from './townsquareGoal';
import { getTownsquareGoalUpdateLink, isTownsquareGoalUpdate } from './townsquareGoalUpdate';
import { getTownsquareProjectLink, isTownsquareProject } from './townsquareProject';
import {
	getTownsquareProjectUpdateLink,
	isTownsquareProjectUpdate,
} from './townsquareProjectUpdate';

const functionMap: EntityFunctionMap = {
	AgentStudioAssistant: {
		validate: isAgentStudioAssistant,
		getLink: getRovoAgentLink,
	},
	AgentStudioServiceAgent: {
		validate: isAgentStudioServiceAgent,
		getLink: getRovoAgentLink,
	},
	AtlassianAccountUser: {
		validate: isAtlassianAccountUser,
		getLink: getAtlassianAccountUserLink,
	},
	AtlassianUser: {
		validate: isAtlassianUser,
		getLink: getAtlassianUserLink,
	},
	BitbucketRepository: {
		validate: isBitbucketRepository,
		getLink: getBitbucketRepositoryLink,
	},
	CompassComponent: {
		validate: isCompassComponent,
		getLink: getCompassComponentLink,
	},
	CompassLinkNode: {
		validate: isCompassComponentLink,
		getLink: getCompassComponentLinkLink,
	},
	CompassScorecard: {
		validate: isCompassScorecard,
		getLink: getCompassScorecardLink,
	},
	JiraPlatformComment: {
		validate: isJiraPlatformComment,
		getLink: getJiraPlatformCommentLink,
	},
	ConfluencePage: {
		validate: isConfluencePage,
		getLink: getConfluencePageLink,
	},
	ConfluenceBlogPost: {
		validate: isConfluenceBlogPost,
		getLink: getConfluenceBlogPostLink,
	},
	ConfluenceComment: {
		validate: isConfluenceComment,
		getLink: getConfluenceCommentLink,
	},
	ConfluenceInlineComment: {
		validate: isConfluenceInlineComment,
		getLink: getConfluenceInlineCommentLink,
	},
	ConfluenceFooterComment: {
		validate: isConfluenceFooterComment,
		getLink: getConfluenceFooterCommentLink,
	},
	ConfluenceDatabase: {
		validate: isConfluenceDatabase,
		getLink: getConfluenceDatabaseLink,
	},
	ConfluenceEmbed: {
		validate: isConfluenceEmbed,
		getLink: getConfluenceEmbedLink,
	},
	ConfluenceFolder: {
		validate: isConfluenceFolder,
		getLink: getConfluenceFolderLink,
	},
	ConfluenceSpace: {
		validate: isConfluenceSpace,
		getLink: getConfluenceSpaceLink,
	},
	ConfluenceWhiteboard: {
		validate: isConfluenceWhiteboard,
		getLink: getConfluenceWhiteboardLink,
	},
	ExternalBranch: {
		validate: isExternalBranch,
		getLink: getExternalBranchLink,
	},
	ExternalCalendarEvent: {
		validate: isExternalCalendarEvent,
		getLink: getExternalCalendarEventLink,
	},
	ExternalCommit: {
		validate: isExternalCommit,
		getLink: getExternalCommitLink,
	},
	ExternalDocument: {
		validate: isExternalDocument,
		getLink: getExternalDocumentLink,
	},
	ExternalMessage: {
		validate: isExternalMessage,
		getLink: getExternalMessageLink,
	},
	ExternalPullRequest: {
		validate: isExternalPullRequest,
		getLink: getExternalPullRequestLink,
	},
	ExternalRepository: {
		validate: isExternalRepository,
		getLink: getExternalRepositoryLink,
	},
	ExternalWorkItem: {
		validate: isExternalWorkItem,
		getLink: getExternalWorkItemLink,
	},
	JiraAlignAggProject: {
		validate: isJiraAlignProject,
		getLink: getJiraAlignProjectLink,
	},
	JiraBoard: {
		validate: isJiraBoard,
		getLink: getJiraBoardLink,
	},
	JiraComment: {
		validate: isJiraComment,
		getLink: getJiraCommentLink,
	},
	JiraServiceManagementComment: {
		validate: isJiraServiceManagementComment,
		getLink: getJiraServiceManagementCommentLink,
	},
	JiraCustomRemoteIssueLink: {
		validate: isJiraCustomRemoteIssueLink,
		getLink: getJiraCustomRemoteIssueLink,
	},
	JiraIssue: {
		validate: isJiraIssue,
		getLink: getJiraIssueLink,
	},
	JiraIssueRemoteIssueLink: {
		validate: isJiraIssueRemoteIssueLink,
		getLink: getJiraIssueRemoteIssueLink,
	},
	JiraPostIncidentReviewLink: {
		validate: isJiraPostIncidentReviewLink,
		getLink: getJiraPostIncidentReviewLink,
	},
	JiraProject: {
		validate: isJiraProject,
		getLink: getJiraProjectLink,
	},
	JiraWebRemoteIssueLink: {
		validate: isJiraWebRemoteIssueLink,
		getLink: getJiraWebRemoteIssueLink,
	},
	LoomComment: {
		validate: isLoomComment,
		getLink: getLoomCommentLink,
	},
	LoomSpace: {
		validate: isLoomSpace,
		getLink: getLoomSpaceLink,
	},
	LoomVideo: {
		validate: isLoomVideo,
		getLink: getLoomVideoLink,
	},
	MercuryFocusArea: {
		validate: isMercuryFocusArea,
		getLink: getMercuryFocusAreaLink,
	},
	TeamV2: {
		validate: isTeamV2,
		getLink: getTeamV2Link,
	},
	TownsquareComment: {
		validate: isTownsquareComment,
		getLink: getTownsquareCommentLink,
	},
	TownsquareGoal: {
		validate: isTownsquareGoal,
		getLink: getTownsquareGoalLink,
	},
	TownsquareGoalUpdate: {
		validate: isTownsquareGoalUpdate,
		getLink: getTownsquareGoalUpdateLink,
	},
	TownsquareProject: {
		validate: isTownsquareProject,
		getLink: getTownsquareProjectLink,
	},
	TownsquareProjectUpdate: {
		validate: isTownsquareProjectUpdate,
		getLink: getTownsquareProjectUpdateLink,
	},
};

export const getLinkFromEntityNode = (
	entity: unknown,
	orgHomeUrl: string,
	cloudId: string,
): string | null => {
	if (
		!(
			entity !== null &&
			typeof entity === 'object' &&
			'__typename' in entity &&
			typeof entity.__typename === 'string'
		)
	) {
		return null;
	}

	const typename = entity.__typename;

	if (!(typename in functionMap)) {
		return null;
	}

	const { validate, getLink } = functionMap[typename as keyof typeof functionMap];

	if (!validate(entity)) {
		return null;
	}
	return getLink(entity as any, orgHomeUrl, cloudId);
};

export const convertEntityTypenameToHeaderName = (entity: EntityTypename): string => {
	switch (entity) {
		case 'AgentStudioAssistant':
		case 'AgentStudioServiceAgent':
			return 'Rovo Agents';
		case 'AtlassianUser':
		case 'AtlassianAccountUser':
			return 'Users';
		case 'BitbucketRepository':
			return 'Bitbucket Repository';
		case 'CompassComponent':
			return 'Compass Component';
		case 'CompassLinkNode':
			return 'Compass Component Link';
		case 'CompassScorecard':
			return 'Compass Scorecard';
		case 'ConfluencePage':
			return 'Pages';
		case 'ConfluenceBlogPost':
			return 'Blog Posts';
		case 'ConfluenceComment':
		case 'ConfluenceInlineComment':
		case 'ConfluenceFooterComment':
			return 'Confluence Comments';
		case 'ConfluenceDatabase':
			return 'Confluence Database';
		case 'ConfluenceEmbed':
			return 'Confluence Embed';
		case 'ConfluenceFolder':
			return 'Confluence Folder';
		case 'ConfluenceSpace':
			return 'Confluence Space';
		case 'ConfluenceWhiteboard':
			return 'Confluence Whiteboard';
		case 'ExternalBranch':
			return 'External Branch';
		case 'ExternalCalendarEvent':
			return 'External Calendar Event';
		case 'ExternalCommit':
			return 'External Commit';
		case 'ExternalDocument':
			return 'External Document';
		case 'ExternalPullRequest':
			return 'External Pull Request';
		case 'ExternalRepository':
			return 'External Repository';
		case 'ExternalWorkItem':
			return 'External Work Item';
		case 'JiraAlignAggProject':
			return 'Jira Align Project';
		case 'JiraBoard':
			return 'Jira Board';
		case 'JiraComment':
		case 'JiraServiceManagementComment':
		case 'JiraPlatformComment':
			return 'Jira Comments';
		case 'JiraCustomRemoteIssueLink':
			return 'Jira Custom Remote Issue Link';
		case 'JiraIssue':
			return 'Work Items';
		case 'JiraIssueRemoteIssueLink':
			return 'Jira Issue Remote Issue Link';
		case 'JiraPostIncidentReviewLink':
			return 'Jira Post Incident Review Link';
		case 'JiraProject':
			return 'Jira Project';
		case 'JiraWebRemoteIssueLink':
			return 'Jira Web Remote Issue Link';
		case 'LoomComment':
			return 'Loom Comment';
		case 'LoomSpace':
			return 'Loom Space';
		case 'LoomVideo':
			return 'Loom';
		case 'MercuryFocusArea':
			return 'Focus Areas';
		case 'TeamV2':
			return 'Teams';
		case 'TownsquareComment':
			return 'Townsquare Comments';
		case 'TownsquareGoal':
			return 'Goals';
		case 'TownsquareGoalUpdate':
			return 'Goal Updates';
		case 'TownsquareProject':
			return 'Atlas Projects';
		case 'TownsquareProjectUpdate':
			return 'Project Updates';
		default:
			return entity;
	}
};

export {
	isAtlassianAccountUser,
	isBitbucketRepository,
	isCompassComponent,
	isCompassComponentLink,
	isCompassScorecard,
	isConfluenceBlogPost,
	isConfluenceComment,
	isConfluenceDatabase,
	isConfluenceEmbed,
	isConfluenceFolder,
	isConfluenceFooterComment,
	isConfluenceInlineComment,
	isConfluencePage,
	isConfluenceSpace,
	isConfluenceWhiteboard,
	isExternalBranch,
	isExternalCalendarEvent,
	isExternalCommit,
	isExternalDocument,
	isExternalPullRequest,
	isExternalRepository,
	isExternalWorkItem,
	isJiraAlignProject,
	isJiraBoard,
	isJiraComment,
	isJiraCustomRemoteIssueLink,
	isJiraIssue,
	isJiraIssueRemoteIssueLink,
	isJiraPostIncidentReviewLink,
	isJiraProject,
	isJiraPlatformComment,
	isJiraServiceManagementComment,
	isJiraWebRemoteIssueLink,
	isLoomComment,
	isLoomSpace,
	isLoomVideo,
	isMercuryFocusArea,
	isTeamV2,
	isTownsquareComment,
	isTownsquareGoal,
	isTownsquareGoalUpdate,
	isTownsquareProject,
	isTownsquareProjectUpdate,
	getAtlassianUserLink,
	isAtlassianUser,
};
